import { useQuery } from '@tanstack/react-query';
import request from '@utils/request';
import moment from 'moment';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { SPA_PACKAGE_TIMESLOT_AVAILABILITY_TIMEOUT } from './consts';

export type BulkSpaPackageConfigGroupsEntry = {
  hasAvailability?: boolean;
  configurationGroups?: ConfigurationGroupType[];
  error?: boolean;
};

export type BulkSpaPackageConfigGroups = Record<string, BulkSpaPackageConfigGroupsEntry>;

export function useGetBulkSpaPackageConfigGroups(
  propertyId: number,
  languageCode: string,
  arrivalDate: string,
  numberOfGuests: number,
  packageIds: Array<number | string>,
  enabled = true
) {
  const normalisedIds = packageIds.map(String).sort();

  return useQuery({
    queryKey: getBulkSpaPackageConfigGroupsKey(
      propertyId,
      languageCode,
      arrivalDate,
      numberOfGuests,
      normalisedIds
    ),
    queryFn: () =>
      getBulkSpaPackageConfigGroups(
        propertyId,
        languageCode,
        arrivalDate,
        numberOfGuests,
        normalisedIds
      ),
    cacheTime: 60000,
    staleTime: 60000,
    enabled: enabled && !!propertyId && normalisedIds.length > 0,
  });
}

export const getBulkSpaPackageConfigGroupsKey = (
  propertyId: number,
  languageCode: string,
  arrivalDate: string,
  numberOfGuests: number,
  packageIds: string[]
) => [
  'spaPackages',
  'bulkConfigGroups',
  propertyId,
  languageCode,
  arrivalDate,
  numberOfGuests,
  packageIds.join(','),
];

export const getBulkSpaPackageConfigGroups = async (
  propertyId: number,
  languageCode: string,
  arrivalDate: string,
  numberOfGuests: number,
  packageIds: Array<number | string>
): Promise<BulkSpaPackageConfigGroups> => {
  const date = moment(arrivalDate).format('YYYY-MM-DD');
  const { data } = await request.get(
    `${propertyId}/spa-packages/${languageCode}/configuration-groups/availability`,
    {
      params: {
        date,
        numberOfGuests,
        packageIds: packageIds.map(String).join(','),
      },
      timeout: SPA_PACKAGE_TIMESLOT_AVAILABILITY_TIMEOUT,
    }
  );

  return (data ?? {}) as BulkSpaPackageConfigGroups;
};
